// SPDX-License-Identifier: AGPL-3.0-or-later

import {FLUXER_TAG_LABEL} from '@app/features/app/config/I18nDisplayConstants';
import Authentication from '@app/features/auth/state/Authentication';
import {canReportMessage} from '@app/features/channel/components/MessageActionUtils';
import {TransferOwnershipModal} from '@app/features/guild/components/modals/TransferOwnershipModal';
import Guilds from '@app/features/guild/state/Guilds';
import {
	resolveGuildModerationCapabilities,
	resolveGuildScopedModerationActionKeys,
} from '@app/features/guild/utils/GuildModerationCapabilityUtils';
import type {GuildMember} from '@app/features/member/models/GuildMember';
import GuildMembers from '@app/features/member/state/GuildMembers';
import type {Message} from '@app/features/messaging/models/Message';
import {BanMemberModal} from '@app/features/moderation/components/modals/BanMemberModal';
import {KickMemberModal} from '@app/features/moderation/components/modals/KickMemberModal';
import {RemoveTimeoutModal} from '@app/features/moderation/components/modals/RemoveTimeoutModal';
import {TimeoutMemberSheet} from '@app/features/moderation/components/modals/TimeoutMemberSheet';
import {openReportMessageModal, openReportUserModal} from '@app/features/moderation/utils/ReportActionUtils';
import {useRoleHierarchy} from '@app/features/permissions/hooks/useRoleHierarchy';
import Permission from '@app/features/permissions/state/Permission';
import * as PermissionUtils from '@app/features/permissions/utils/PermissionUtils';
import Relationships from '@app/features/relationship/state/Relationships';
import * as RelationshipActionUtils from '@app/features/relationship/utils/RelationshipActionUtils';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import {MenuBottomSheet, type MenuGroupType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import {ChangeNicknameModal} from '@app/features/user/components/modals/ChangeNicknameModal';
import styles from '@app/features/user/components/modals/UserProfileActionsSheet.module.css';
import type {User} from '@app/features/user/models/User';
import {Permissions} from '@app/vendor/constants/ChannelConstants';
import {RelationshipTypes} from '@app/vendor/constants/UserConstants';
import {useLingui} from '@lingui/react/macro';
import {
	ClockIcon,
	CopyIcon,
	CrownIcon,
	FlagIcon,
	GavelIcon,
	GlobeIcon,
	IdentificationCardIcon,
	PencilIcon,
	ProhibitIcon,
	SignOutIcon,
	UserMinusIcon,
} from '@phosphor-icons/react';

import {observer} from 'mobx-react-lite';

interface UserProfileActionsSheetProps {
	isOpen: boolean;
	onClose: () => void;
	user: User;
	isCurrentUser?: boolean;
	hasGuildProfile?: boolean;
	showGlobalProfile?: boolean;
	onToggleProfileView?: () => void;
	guildId?: string;
	guildMember?: GuildMember | null;
	message?: Message;
}
export const UserProfileActionsSheet: React.FC<UserProfileActionsSheetProps> = observer(
	({
		isOpen,
		onClose,
		user,
		isCurrentUser = false,
		hasGuildProfile = false,
		showGlobalProfile = false,
		onToggleProfileView,
		guildId,
		guildMember,
		message,
	}) => {
		const {t, i18n} = useLingui();
		const relationshipType = Relationships.getRelationship(user.id)?.type;
		const guild = guildId ? Guilds.getGuild(guildId) : null;
		const member = guildMember ?? (guildId ? GuildMembers.getMember(guildId, user.id) : null);
		const currentUserId = Authentication.currentUserId;
		const canKickMembers = guildId ? Permission.can(Permissions.KICK_MEMBERS, {guildId}) : false;
		const canBanMembers = guildId ? Permission.can(Permissions.BAN_MEMBERS, {guildId}) : false;
		const canModerateMembers = guildId ? Permission.can(Permissions.MODERATE_MEMBERS, {guildId}) : false;
		const hasChangeNicknamePermission = guildId ? Permission.can(Permissions.CHANGE_NICKNAME, {guildId}) : false;
		const hasManageNicknamesPermission = guildId ? Permission.can(Permissions.MANAGE_NICKNAMES, {guildId}) : false;
		const isOwner = guild?.ownerId === currentUserId;
		const {canManageTarget} = useRoleHierarchy(guild);
		const guildSnapshot = guild?.toJSON();
		const targetHasModerateMembersPermission =
			guildSnapshot !== undefined && PermissionUtils.can(Permissions.MODERATE_MEMBERS, user.id, guildSnapshot);
		const {canKick, canBan, canTimeout} = resolveGuildModerationCapabilities({
			isCurrentUser,
			canManageTarget: canManageTarget(user.id),
			canKickMembers,
			canBanMembers,
			canModerateMembers,
			targetHasModerateMembersPermission,
		});
		const canTransfer = !isCurrentUser && !user.bot && isOwner && member;
		const memberIsTimedOut = member?.isTimedOut() ?? false;
		const canManageNicknames =
			member &&
			((isCurrentUser && hasChangeNicknamePermission && !memberIsTimedOut) ||
				(hasManageNicknamesPermission && canManageTarget(user.id)));
		const handleCopyFluxerTag = () => {
			TextCopyCommands.copy(i18n, `${user.username}#${user.discriminator}`, true);
			onClose();
		};
		const handleCopyUserId = () => {
			TextCopyCommands.copy(i18n, user.id, true);
			onClose();
		};
		const handleRemoveFriend = () => {
			onClose();
			RelationshipActionUtils.showRemoveFriendConfirmation(i18n, user);
		};
		const handleBlockUser = () => {
			onClose();
			RelationshipActionUtils.showBlockUserConfirmation(i18n, user);
		};
		const handleUnblockUser = () => {
			onClose();
			RelationshipActionUtils.showUnblockUserConfirmation(i18n, user);
		};
		const handleChangeNickname = () => {
			if (!guildId || !member) return;
			onClose();
			ModalCommands.push(modal(() => <ChangeNicknameModal guildId={guildId} user={user} member={member} />));
		};
		const handleKickMember = () => {
			if (!guildId) return;
			onClose();
			ModalCommands.push(modal(() => <KickMemberModal guildId={guildId} targetUser={user} />));
		};
		const handleBanMember = () => {
			if (!guildId) return;
			onClose();
			ModalCommands.push(modal(() => <BanMemberModal guildId={guildId} targetUser={user} />));
		};
		const handleTimeoutMember = () => {
			if (!guildId || !member) return;
			onClose();
			ModalCommands.push(
				modal(() => (
					<TimeoutMemberSheet
						isOpen={true}
						onClose={() => ModalCommands.pop()}
						guildId={guildId}
						targetUser={user}
						targetMember={member}
					/>
				)),
			);
		};
		const handleRemoveTimeout = () => {
			if (!guildId) return;
			onClose();
			ModalCommands.push(modal(() => <RemoveTimeoutModal guildId={guildId} targetUser={user} />));
		};
		const handleTransferOwnership = () => {
			if (!guildId || !member) return;
			onClose();
			ModalCommands.push(
				modal(() => <TransferOwnershipModal guildId={guildId} targetUser={user} targetMember={member} />),
			);
		};
		const handleReportUser = () => {
			onClose();
			openReportUserModal({i18n, user, guildId, message});
		};
		const handleReportMessage = () => {
			if (!message || !canReportMessage(message)) {
				return;
			}
			onClose();
			openReportMessageModal(message);
		};
		const menuGroups: Array<MenuGroupType> = [];
		if (hasGuildProfile && onToggleProfileView) {
			menuGroups.push({
				items: [
					{
						icon: <GlobeIcon className={styles.icon} />,
						label: showGlobalProfile ? t`View Community Profile` : t`View Global Profile`,
						onClick: () => {
							onToggleProfileView();
							onClose();
						},
					},
				],
			});
		}
		menuGroups.push({
			items: [
				{
					icon: <CopyIcon className={styles.icon} />,
					label: t`Copy ${FLUXER_TAG_LABEL}`,
					onClick: handleCopyFluxerTag,
				},
				{
					icon: <IdentificationCardIcon className={styles.icon} />,
					label: t`Copy User ID`,
					onClick: handleCopyUserId,
				},
			],
		});
		if (guildId) {
			const guildItems = [];
			if (member && canManageNicknames) {
				guildItems.push({
					icon: <PencilIcon className={styles.icon} />,
					label: isCurrentUser ? t`Change Nickname` : t`Change Nickname`,
					onClick: handleChangeNickname,
				});
			}
			if (guildItems.length > 0) {
				menuGroups.push({items: guildItems});
			}
			if (member && canTransfer) {
				menuGroups.push({
					items: [
						{
							icon: <CrownIcon className={styles.icon} />,
							label: t`Transfer Ownership`,
							onClick: handleTransferOwnership,
							danger: true,
						},
					],
				});
			}
			const moderationActionKeys = resolveGuildScopedModerationActionKeys({
				hasMember: member != null,
				canTimeout,
				isTimedOut: memberIsTimedOut,
				canKick,
				canBan,
			});
			if (moderationActionKeys.length > 0) {
				const moderationItems = [];
				for (const actionKey of moderationActionKeys) {
					switch (actionKey) {
						case 'timeout':
							moderationItems.push({
								icon: <ClockIcon className={styles.icon} />,
								label: t`Timeout`,
								onClick: handleTimeoutMember,
								danger: true,
							});
							break;
						case 'remove_timeout':
							moderationItems.push({
								icon: <ClockIcon className={styles.icon} />,
								label: t`Remove Timeout`,
								onClick: handleRemoveTimeout,
								danger: true,
							});
							break;
						case 'kick':
							moderationItems.push({
								icon: <SignOutIcon className={styles.icon} />,
								label: t`Kick`,
								onClick: handleKickMember,
								danger: true,
							});
							break;
						case 'ban':
							moderationItems.push({
								icon: <GavelIcon className={styles.icon} />,
								label: t`Ban`,
								onClick: handleBanMember,
								danger: true,
							});
							break;
					}
				}
				menuGroups.push({items: moderationItems});
			}
		}
		if (!isCurrentUser) {
			if (relationshipType === RelationshipTypes.FRIEND) {
				menuGroups.push({
					items: [
						{
							icon: <UserMinusIcon className={styles.icon} />,
							label: t`Remove Friend`,
							onClick: handleRemoveFriend,
							danger: true,
						},
					],
				});
			}
			const reportBlockItems = [
				...(message && canReportMessage(message)
					? [
							{
								icon: <FlagIcon className={styles.icon} />,
								label: t`Report Message`,
								onClick: handleReportMessage,
								danger: true,
							},
						]
					: []),
				{
					icon: <FlagIcon className={styles.icon} />,
					label: t`Report User`,
					onClick: handleReportUser,
					danger: true,
				},
			];
			if (!user.system) {
				if (relationshipType !== RelationshipTypes.BLOCKED) {
					reportBlockItems.push({
						icon: <ProhibitIcon className={styles.icon} />,
						label: t`Block`,
						onClick: handleBlockUser,
						danger: true,
					});
				} else {
					reportBlockItems.push({
						icon: <ProhibitIcon className={styles.icon} />,
						label: t`Unblock`,
						onClick: handleUnblockUser,
						danger: false,
					});
				}
			}
			menuGroups.push({items: reportBlockItems});
		}
		return <MenuBottomSheet isOpen={isOpen} onClose={onClose} groups={menuGroups} />;
	},
);
